import React, { useState, useEffect, useRef, useCallback } from "react";
import { Card, Form, Button, Alert, Spinner, Badge, ListGroup } from "react-bootstrap";
import "./NotificationCenter.css";
import { userService } from "../../../components/User/services/userService";
import { User } from "../../Data/Users";
import { notificationService } from "../../../components/Notifications/services/notificationService";

interface NotificationCenterProps {
    sendingNotification: boolean;
    setSendingNotification: (sending: boolean) => void;
    error: string | null;
    setError: (error: string | null) => void;
    successMessage: string | null;
    setSuccessMessage: (message: string | null) => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
    sendingNotification,
    setSendingNotification,
    error,
    setError,
    successMessage,
    setSuccessMessage,
}) => {
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [notificationType, setNotificationType] = useState<"broadcast" | "targeted">("broadcast");
    
    // User selection states
    const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMoreUsers, setHasMoreUsers] = useState(true);
    const [showDropdown, setShowDropdown] = useState(false);
    
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const observerTarget = useRef<HTMLDivElement>(null);
    const debounceTimer = useRef<NodeJS.Timeout>();

    useEffect(() => {
        if (successMessage || error) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
                setError(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, error, setSuccessMessage, setError]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Fetch users with search and pagination
    const fetchUsers = useCallback(async (page: number, search: string, append: boolean = false) => {
        try {
            setLoadingUsers(true);
            const response = await userService.getEnhancedUsers({
                page,
                pageSize: 20,
                name: search || undefined,
                email: search || undefined,
            });

            const newUsers = response.data.users.users || [];
            
            if (append) {
                setUsers(prev => [...prev, ...newUsers]);
            } else {
                setUsers(newUsers);
            }
            const PAGE_SIZE = 20;
            setHasMoreUsers(newUsers.length === PAGE_SIZE);
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    // Debounced search
    useEffect(() => {
        if (notificationType === "targeted" && showDropdown) {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }

            debounceTimer.current = setTimeout(() => {
                setCurrentPage(1);
                fetchUsers(1, searchQuery, false);
            }, 300);

            return () => {
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                }
            };
        }
    }, [searchQuery, notificationType, showDropdown, fetchUsers]);

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!observerTarget.current || !hasMoreUsers || loadingUsers) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMoreUsers && !loadingUsers) {
                    const nextPage = currentPage + 1;
                    setCurrentPage(nextPage);
                    fetchUsers(nextPage, searchQuery, true);
                }
            },
            { threshold: 0.1 }
        );

        observer.observe(observerTarget.current);

        return () => observer.disconnect();
    }, [currentPage, hasMoreUsers, loadingUsers, searchQuery, fetchUsers]);

    const handleUserSelect = (user: User) => {
        if (!selectedUsers.find(u => u.id === user.id)) {
            setSelectedUsers([...selectedUsers, user]);
        }
        setSearchQuery("");
        setShowDropdown(false);
    };

    const handleUserRemove = (userId: number) => {
        setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
    };

    const handleSendNotification = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!title.trim() || !body.trim()) {
            setError("Title and body are required");
            return;
        }

        if (notificationType === "targeted" && selectedUsers.length === 0) {
            setError("Please select at least one user for targeted notifications");
            return;
        }

        try {
            setSendingNotification(true);
            setError(null);
            setSuccessMessage(null);

            const userIds = notificationType === "targeted" 
                ? selectedUsers.map(u => u.id.toString())
                : undefined;

            const response = await notificationService.sendNotification({
                title: title.trim(),
                body: body.trim(),
                userIds,
            });

            setSuccessMessage(
                `Notification sent successfully! ${response.data.successCount} sent, ${response.data.failureCount} failed.`
            );
            
            // Reset form
            setTitle("");
            setBody("");
            setSelectedUsers([]);
            setSearchQuery("");
        } catch (err: any) {
            console.error("Error sending notification:", err);
            setError(
                err.response?.data?.error || 
                "Failed to send notification. Please try again."
            );
        } finally {
            setSendingNotification(false);
        }
    };

    const handleClear = () => {
        setTitle("");
        setBody("");
        setSelectedUsers([]);
        setSearchQuery("");
        setError(null);
        setSuccessMessage(null);
    };

    return (
        <div className="notification-center">
            <Card className="notification-card shadow-sm">
                <Card.Header className="notification-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h4 className="mb-1">Notification Center</h4>
                            <p className="text-muted mb-0">
                                Send notifications to users
                            </p>
                        </div>
                        <Badge 
                            bg={notificationType === "broadcast" ? "primary" : "info"}
                            className="notification-type-badge"
                        >
                            {notificationType === "broadcast" ? "Broadcast Mode" : "Targeted Mode"}
                        </Badge>
                    </div>
                </Card.Header>

                <Card.Body className="notification-body">
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    <Form onSubmit={handleSendNotification}>
                        <Form.Group className="mb-3">
                            <Form.Label>Notification Type</Form.Label>
                            <div className="d-flex gap-3">
                                <Form.Check
                                    type="radio"
                                    id="broadcast"
                                    label="Broadcast to All Users"
                                    name="notificationType"
                                    checked={notificationType === "broadcast"}
                                    onChange={() => {
                                        setNotificationType("broadcast");
                                        setSelectedUsers([]);
                                        setSearchQuery("");
                                    }}
                                />
                                <Form.Check
                                    type="radio"
                                    id="targeted"
                                    label="Send to Specific Users"
                                    name="notificationType"
                                    checked={notificationType === "targeted"}
                                    onChange={() => setNotificationType("targeted")}
                                />
                            </div>
                        </Form.Group>

                        {notificationType === "targeted" && (
                            <Form.Group className="mb-3">
                                <Form.Label>Select Users</Form.Label>
                                
                                {/* Selected Users Pills */}
                                {selectedUsers.length > 0 && (
                                    <div className="selected-users-container mb-2">
                                        {selectedUsers.map(user => (
                                            <Badge 
                                                key={user.id} 
                                                bg="primary" 
                                                className="selected-user-pill"
                                            >
                                                {user.name || user.username}
                                                <button
                                                    type="button"
                                                    className="remove-user-btn"
                                                    onClick={() => handleUserRemove(user.id)}
                                                    disabled={sendingNotification}
                                                >
                                                    ×
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}

                                {/* Search Input with Dropdown */}
                                <div className="user-search-container" ref={dropdownRef}>
                                    <Form.Control
                                        ref={searchInputRef}
                                        type="text"
                                        placeholder="Search users by name or email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        onFocus={() => {
                                            setShowDropdown(true);
                                            if (users.length === 0) {
                                                fetchUsers(1, searchQuery, false);
                                            }
                                        }}
                                        disabled={sendingNotification}
                                        className="user-search-input"
                                    />
                                    
                                    {showDropdown && (
                                        <div className="user-dropdown">
                                            {loadingUsers && users.length === 0 ? (
                                                <div className="text-center py-3">
                                                    <Spinner animation="border" size="sm" />
                                                </div>
                                            ) : users.length === 0 ? (
                                                <div className="text-center py-3 text-muted">
                                                    No users found
                                                </div>
                                            ) : (
                                                <ListGroup variant="flush">
                                                    {users
                                                        .filter(user => !selectedUsers.find(u => u.id === user.id))
                                                        .map(user => (
                                                            <ListGroup.Item
                                                                key={user.id}
                                                                action
                                                                onClick={() => handleUserSelect(user)}
                                                                className="user-dropdown-item"
                                                            >
                                                                <div className="user-info">
                                                                    <div className="user-name">
                                                                        {user.name || user.username}
                                                                    </div>
                                                                    <div className="user-email">
                                                                        {user.email}
                                                                    </div>
                                                                </div>
                                                            </ListGroup.Item>
                                                        ))}
                                                    
                                                    {/* Intersection Observer Target */}
                                                    {hasMoreUsers && (
                                                        <div ref={observerTarget} className="text-center py-2">
                                                            {loadingUsers && (
                                                                <Spinner animation="border" size="sm" />
                                                            )}
                                                        </div>
                                                    )}
                                                </ListGroup>
                                            )}
                                        </div>
                                    )}
                                </div>
                                
                                <Form.Text className="text-muted">
                                    {selectedUsers.length} user(s) selected
                                </Form.Text>
                            </Form.Group>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Notification Title *</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter notification title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={sendingNotification}
                                maxLength={100}
                            />
                            <Form.Text className="text-muted">
                                {title.length}/100 characters
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Notification Body *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Enter notification message"
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                disabled={sendingNotification}
                                maxLength={500}
                            />
                            <Form.Text className="text-muted">
                                {body.length}/500 characters
                            </Form.Text>
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2">
                            <Button
                                variant="secondary"
                                onClick={handleClear}
                                disabled={sendingNotification}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={sendingNotification || !title.trim() || !body.trim()}
                            >
                                {sendingNotification ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Sending...
                                    </>
                                ) : (
                                    "Send Notification"
                                )}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </div>
    );
};

export default NotificationCenter;